import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import Nav from '../components/Nav';

const VOICES = [
  { value: 'aura-2-thalia-en', label: 'Female voice' },
  { value: 'aura-2-orion-en',  label: 'Male voice' },
];

const BLANK = {
  name: '', industry: '', product_description: '',
  persona_role: 'gatekeeper', persona_name: '', persona_title: '',
  company_name: '', tts_voice: 'aura-asteria-en',
  personality_traits: [''], key_objections: [''],
  mode: 'sales',
};

const ROLE_STYLES = {
  gatekeeper:     { bg: '#FFFBEB', border: '#FDE68A', text: '#D97706' },
  decision_maker: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8' },
  custom:         { bg: '#F0FDF4', border: '#BBF7D0', text: '#15803D' },
};

function RoleBadge({ role }) {
  const s = ROLE_STYLES[role] || ROLE_STYLES.custom;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
      padding: '3px 8px', borderRadius: 999, background: s.bg, color: s.text, border: `1px solid ${s.border}` }}>
      {role.replace('_', ' ')}
    </span>
  );
}

function ModeBadge({ mode }) {
  if (mode !== 'interview') return null;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
      padding: '3px 8px', borderRadius: 999, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
      🎯 interview prep
    </span>
  );
}

export default function Scenarios() {
  const nav = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState('mine');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getScenarios().then(setScenarios).catch(() => {});
    api.getTemplates().then(setTemplates).catch(() => {});
  }, []);

  function set(k, v) {
    setForm(f => {
      const update = { ...f, [k]: v };
      if (k === 'persona_role') {
        update.mode = v === 'interviewer' ? 'interview' : 'sales';
      }
      return update;
    });
  }
  function setItem(key, i, val) { setForm(f => { const a = [...f[key]]; a[i] = val; return { ...f, [key]: a }; }); }
  function addItem(key) { setForm(f => ({ ...f, [key]: [...f[key], ''] })); }
  function removeItem(key, i) { setForm(f => ({ ...f, [key]: f[key].filter((_, j) => j !== i) })); }

  function useTemplate(t) {
    setForm({
      name: t.name.replace(/^[^\w]+/, ''),
      industry: t.industry,
      product_description: '',
      persona_role: t.persona_role,
      persona_name: t.persona_name,
      persona_title: t.persona_title,
      company_name: t.company_name,
      tts_voice: t.tts_voice,
      mode: t.mode || 'sales',
      personality_traits: t.personality_traits.length ? t.personality_traits : [''],
      key_objections: t.key_objections.length ? t.key_objections : [''],
    });
    setTab('mine');
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function submit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const payload = { ...form, personality_traits: form.personality_traits.filter(Boolean), key_objections: form.key_objections.filter(Boolean) };
      const s = await api.createScenario(payload);
      setScenarios(prev => [...prev, s]);
      setShowForm(false);
      setForm(BLANK);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function del(id) {
    if (!window.confirm('Delete this scenario?')) return;
    await api.deleteScenario(id);
    setScenarios(prev => prev.filter(s => s.id !== id));
  }

  return (
    <div style={S.page}>
      <Nav />
      <div style={S.content}>
        <div style={S.topRow}>
          <div>
            <div style={S.pageTitle}>Scenarios</div>
            <div style={S.pageSub}>Build and manage your practice scenarios.</div>
          </div>
          <button style={S.addBtn} onClick={() => { setForm(BLANK); setShowForm(v => !v); setTab('mine'); }}>
            {showForm ? '✕ Cancel' : '+ New Scenario'}
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <form onSubmit={submit} style={S.formCard}>
            {form.industry && (
              <div style={S.templateHint}>
                ✏️ Template pre-filled — add your product description and save.
              </div>
            )}
            <div style={S.formGrid}>
              <div style={S.formSection}>
                <div style={S.formSectionTitle}>What you're selling</div>
                <label style={S.label}>Scenario name</label>
                <input style={S.input} placeholder="e.g. Dental Clinic Cold Call" value={form.name} onChange={e => set('name', e.target.value)} required />
                <label style={S.label}>Industry</label>
                <input style={S.input} placeholder="e.g. dental clinic, SaaS company, real estate" value={form.industry} onChange={e => set('industry', e.target.value)} required />
                <label style={S.label}>Your product (describe the pain it solves)</label>
                <textarea style={{ ...S.input, resize: 'vertical' }} rows={3} placeholder="e.g. An AI that answers calls after hours and books appointments automatically — so no patient is lost to voicemail." value={form.product_description} onChange={e => set('product_description', e.target.value)} required />
              </div>

              <div style={S.formSection}>
                <div style={S.formSectionTitle}>Persona</div>
                <div style={S.inlineRow}>
                  <div style={{ flex: 1 }}>
                    <label style={S.label}>Name</label>
                    <input style={S.input} placeholder="Maria" value={form.persona_name} onChange={e => set('persona_name', e.target.value)} required />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={S.label}>Title</label>
                    <input style={S.input} placeholder="Front Desk Receptionist" value={form.persona_title} onChange={e => set('persona_title', e.target.value)} required />
                  </div>
                </div>
                <label style={S.label}>Company</label>
                <input style={S.input} placeholder="Bright Smile Dental" value={form.company_name} onChange={e => set('company_name', e.target.value)} required />
                <div style={S.inlineRow}>
                  <div style={{ flex: 1 }}>
                    <label style={S.label}>Role</label>
                    <select style={S.select} value={form.persona_role} onChange={e => set('persona_role', e.target.value)}>
                      <option value="gatekeeper">Gatekeeper</option>
                      <option value="decision_maker">Decision Maker</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={S.label}>Voice</label>
                    <select style={S.select} value={form.tts_voice} onChange={e => set('tts_voice', e.target.value)}>
                      {VOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div style={S.formSection}>
              <div style={S.formSectionTitle}>Personality traits</div>
              {form.personality_traits.map((t, i) => (
                <div key={i} style={S.listRow}>
                  <input style={{ ...S.input, flex: 1, marginBottom: 0 }} placeholder="e.g. Skeptical of vendors, protective of the doctor's time" value={t} onChange={e => setItem('personality_traits', i, e.target.value)} />
                  <button type="button" style={S.removeBtn} onClick={() => removeItem('personality_traits', i)}>×</button>
                </div>
              ))}
              <button type="button" style={S.addItemBtn} onClick={() => addItem('personality_traits')}>+ Add trait</button>
            </div>

            <div style={S.formSection}>
              <div style={S.formSectionTitle}>{form.persona_role === 'interviewer' ? 'Key questions to practice against' : 'Key objections to practice against'}</div>
              {form.key_objections.map((o, i) => (
                <div key={i} style={S.listRow}>
                  <input style={{ ...S.input, flex: 1, marginBottom: 0 }} placeholder={form.persona_role === 'interviewer' ? '"Tell me about a time you handled conflict"' : '"We already have a system for that"'} value={o} onChange={e => setItem('key_objections', i, e.target.value)} />
                  <button type="button" style={S.removeBtn} onClick={() => removeItem('key_objections', i)}>×</button>
                </div>
              ))}
              <button type="button" style={S.addItemBtn} onClick={() => addItem('key_objections')}>+ Add objection</button>
            </div>

            {error && <div style={S.error}>{error}</div>}
            <div style={S.formActions}>
              <button type="button" style={S.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={S.saveBtn} type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save Scenario'}</button>
            </div>
          </form>
        )}

        {/* Tabs */}
        <div style={S.tabs}>
          {[['mine', `My Scenarios (${scenarios.length})`], ['templates', `Template Library (${templates.length})`]].map(([t, label]) => (
            <button key={t} style={{ ...S.tab, ...(tab === t ? S.tabActive : {}) }} onClick={() => setTab(t)}>{label}</button>
          ))}
        </div>

        {/* My Scenarios */}
        {tab === 'mine' && (
          <div style={S.list}>
            {scenarios.length === 0 && !showForm && (
              <div style={S.emptyState}>
                <div style={S.emptyIcon}>🎭</div>
                <div style={S.emptyTitle}>No scenarios yet</div>
                <div style={S.emptyBody}>Browse the template library to get started, or build your own.</div>
                <button style={S.emptyBtn} onClick={() => setTab('templates')}>Browse templates →</button>
              </div>
            )}
            {scenarios.map(s => (
              <div key={s.id} style={S.card}>
                <div style={S.cardTop}>
                  <div style={{ flex: 1 }}>
                    <div style={S.cardTitleRow}>
                      <span style={S.cardName}>{s.name}</span>
                      <RoleBadge role={s.persona_role} />
                      <ModeBadge mode={s.mode} />
                    </div>
                    <div style={S.cardMeta}>{s.persona_name} · {s.persona_title} · {s.company_name}</div>
                    <div style={S.cardDesc}>{s.product_description}</div>
                  </div>
                  <div style={S.cardActions}>
                    <button style={S.practiceBtn} onClick={() => nav('/practice', { state: { scenarioId: s.id } })}>Practice →</button>
                    <button style={S.deleteBtn} onClick={() => del(s.id)}>Delete</button>
                  </div>
                </div>
                {s.key_objections.length > 0 && (
                  <div style={S.objPills}>
                    {s.key_objections.slice(0, 3).map((o, i) => (
                      <span key={i} style={S.objPill}>"{o}"</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Templates */}
        {tab === 'templates' && (
          <div>
            <div style={S.templateIntro}>Pick a template to pre-fill the form. You'll add your product description before saving.</div>
            <div style={S.list}>
              {templates.map(t => (
                <div key={t.id} style={S.card}>
                  <div style={S.cardTop}>
                    <div style={{ flex: 1 }}>
                      <div style={S.cardTitleRow}>
                        <span style={S.cardName}>{t.name}</span>
                        <RoleBadge role={t.persona_role} />
                        <ModeBadge mode={t.mode} />
                      </div>
                      <div style={S.cardMeta}>{t.persona_name} · {t.persona_title} · {t.company_name}</div>
                    </div>
                    <button style={S.useTemplateBtn} onClick={() => useTemplate(t)}>Use Template →</button>
                  </div>
                  {t.key_objections.length > 0 && (
                    <div>
                      <div style={S.objPillsLabel}>Objections included:</div>
                      <div style={S.objPills}>
                        {t.key_objections.slice(0, 3).map((o, i) => <span key={i} style={S.objPill}>"{o}"</span>)}
                        {t.key_objections.length > 3 && <span style={{ ...S.objPill, color: '#9CA3AF', borderColor: '#E5E7EB' }}>+{t.key_objections.length - 3} more</span>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', background: '#F8F9FB', fontFamily: 'Inter, -apple-system, sans-serif' },
  content: { maxWidth: 900, margin: '0 auto', padding: '32px 24px' },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  pageTitle: { fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 4 },
  pageSub: { fontSize: 14, color: '#9CA3AF' },
  addBtn: { padding: '9px 20px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer', flexShrink: 0 },
  formCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, padding: '28px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  templateHint: { fontSize: 13, color: '#D97706', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', marginBottom: 20 },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, marginBottom: 20 },
  formSection: { marginBottom: 20 },
  formSectionTitle: { fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #F1F3F7' },
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 5, marginTop: 12 },
  input: { width: '100%', padding: '9px 12px', background: '#F8F9FB', border: '1px solid #D1D5DB', borderRadius: 8, color: '#111827', fontSize: 13, boxSizing: 'border-box', marginBottom: 4, outline: 'none', fontFamily: 'inherit' },
  select: { width: '100%', padding: '9px 12px', background: '#F8F9FB', border: '1px solid #D1D5DB', borderRadius: 8, color: '#111827', fontSize: 13, marginBottom: 4 },
  inlineRow: { display: 'flex', gap: 12 },
  listRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 },
  removeBtn: { padding: '6px 10px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, color: '#DC2626', cursor: 'pointer', flexShrink: 0, fontSize: 14 },
  addItemBtn: { background: 'transparent', border: '1px dashed #D1D5DB', borderRadius: 6, color: '#9CA3AF', padding: '6px 14px', cursor: 'pointer', fontSize: 13, marginTop: 4 },
  error: { fontSize: 13, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginBottom: 16 },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
  cancelBtn: { padding: '9px 20px', background: 'transparent', border: '1px solid #E5E7EB', borderRadius: 8, color: '#6B7280', cursor: 'pointer', fontSize: 14 },
  saveBtn: { padding: '9px 24px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  tabs: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #E5E7EB', paddingBottom: 0 },
  tab: { padding: '8px 16px', background: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: '#9CA3AF', cursor: 'pointer', fontSize: 14, fontWeight: 500, marginBottom: -1 },
  tabActive: { color: '#2563EB', borderBottomColor: '#2563EB', fontWeight: 600 },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  emptyState: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, padding: '48px 24px', textAlign: 'center' },
  emptyIcon: { fontSize: 36, marginBottom: 12 },
  emptyTitle: { fontSize: 17, fontWeight: 700, color: '#111827', marginBottom: 6 },
  emptyBody: { fontSize: 14, color: '#9CA3AF', marginBottom: 20 },
  emptyBtn: { padding: '9px 20px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  templateIntro: { fontSize: 14, color: '#6B7280', marginBottom: 16 },
  card: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 },
  cardTitleRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' },
  cardName: { fontSize: 15, fontWeight: 600, color: '#111827' },
  cardMeta: { fontSize: 13, color: '#9CA3AF', marginBottom: 6 },
  cardDesc: { fontSize: 13, color: '#6B7280', lineHeight: 1.5 },
  cardActions: { display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 },
  practiceBtn: { padding: '7px 16px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  deleteBtn: { padding: '7px 16px', background: 'transparent', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#9CA3AF', cursor: 'pointer' },
  useTemplateBtn: { padding: '7px 16px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#2563EB', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  objPills: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 },
  objPillsLabel: { fontSize: 11, color: '#9CA3AF', marginTop: 10, marginBottom: 6 },
  objPill: { fontSize: 12, padding: '3px 10px', background: '#F8F9FB', border: '1px solid #E5E7EB', borderRadius: 999, color: '#6B7280', fontStyle: 'italic' },
};
